import {
  FREE_ACCESS_PACKAGE_IDS,
  FREE_PLAN_PACKAGE_IDS,
  normalizePackageId,
  type AccountDetail,
  type AccountItem,
} from '../models/account.types'

export { FREE_ACCESS_PACKAGE_IDS, FREE_PLAN_PACKAGE_IDS }
export const FREE_PLAN_PACKAGE_ID = 10000126
export const FREE_TRIAL_PACKAGE_ID = 10000127
export const FREE_PLAN_DURATION_DAYS = 90
/** Free keeps this many days of history (the account trims the rest server-side). */
export const FREE_PLAN_DATA_WINDOW_DAYS = 30

export type FreeAccessStatus = 'unknown' | 'active' | 'countdown' | 'ending' | 'ended'

export interface FreeAccessPeriod {
  startedAt: Date | null
  endedAt: Date | null
}

export interface FreeAccessSnapshot extends FreeAccessPeriod {
  status: FreeAccessStatus
  daysLeft: number | null
  elapsedDays: number | null
}

export interface FreePlanLockedRoute {
  routeName: string
  screenKey: string
  requiredPlan: 'pro'
}

type DateSource = Record<string, unknown> | null | undefined

const DAY_MS = 24 * 60 * 60 * 1000
const COUNTDOWN_START_DAY = 60
const ENDING_START_DAY = 83

// Insertion order is the Free sidebar's order: `freePlanLockedScreens` lists the screens in it.
const LOCKED_ROUTE_KEYS: Readonly<Record<string, string>> = {
  Orpheus: 'orpheus',
  WorkflowCreate: 'workflows',
  Workflows: 'workflows',
  Workflow: 'workflows',
  ImpactAnalysis: 'impact',
  ImpactAnalysisDetail: 'impact',
  KPITracking: 'kpi',
  CreativeDashboard: 'creativePerformance',
  CreativeAi: 'creative',
  CreativeAiDetail: 'creative',
  CreativeWatchlist: 'creativeWatchlist',
  Benchmark: 'benchmark',
  PerformanceOverview: 'performanceOverview',
  MobileAppAcquisition: 'appAcquisition',
  PerformanceMaxCampaigns: 'performanceMax',
  LandingPage: 'landingPage',
  PlacementAnalysis: 'placement',
  SearchAdsExcellence: 'search',
  DisplayVideo360: 'displayVideo360',
  DemographicAnalysis: 'demographics',
  FunnelExploration: 'funnel',
  RFMClustering: 'rfm',
  UserBehavior: 'behavior',
  ProductPerformance: 'product',
  CustomerIntelligence: 'customerIntelligence',
  ProductInsights: 'productInsights',
  // A settings pane, not a screen of its own: locked, but never offered as an alternative.
  SettingsCreativeAi: 'creativeSettings',
}

const UNLISTED_SCREEN_KEYS: ReadonlySet<string> = new Set(['creativeSettings'])

const LOCKED_REPORT_TABS: Readonly<Record<string, ReadonlySet<string>>> = {
  budget_allocation: new Set(['Ad Group', 'Ad', 'Key Events']),
}

function asRecord(value: unknown): DateSource {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function firstDate(sources: DateSource[], keys: string[]): Date | null {
  for (const source of sources) {
    for (const key of keys) {
      const value = source?.[key]
      if (!value) continue
      const date = new Date(String(value))
      if (!Number.isNaN(date.getTime())) return date
    }
  }
  return null
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setUTCDate(result.getUTCDate() + days)
  return result
}

export function isFreePlanPackage(packageId: unknown): boolean {
  return Number(packageId) === FREE_PLAN_PACKAGE_ID
}

export function isFreeTrialPackage(packageId: unknown): boolean {
  return Number(packageId) === FREE_TRIAL_PACKAGE_ID
}

export function getAccountDetailPackageId(
  accountDetail: AccountDetail | null | undefined,
): number | null {
  return normalizePackageId(accountDetail?.package)
}

export function freePlanLockedRoute(routeName: unknown): FreePlanLockedRoute | null {
  const normalized = typeof routeName === 'string' ? routeName : ''
  const screenKey = LOCKED_ROUTE_KEYS[normalized]
  return screenKey ? { routeName: normalized, screenKey, requiredPlan: 'pro' } : null
}

/**
 * One entry per locked screen (the first route that opens it), in sidebar order — what the locked
 * screen offers as "also included". Settings panes are locked but deliberately not listed.
 */
export function freePlanLockedScreens(): FreePlanLockedRoute[] {
  const seen = new Set<string>()
  const screens: FreePlanLockedRoute[] = []
  for (const [routeName, screenKey] of Object.entries(LOCKED_ROUTE_KEYS)) {
    if (seen.has(screenKey) || UNLISTED_SCREEN_KEYS.has(screenKey)) continue
    seen.add(screenKey)
    screens.push({ routeName, screenKey, requiredPlan: 'pro' })
  }
  return screens
}

export function isFreePlanLockedReportTab(reportKey: unknown, tabName: unknown): boolean {
  const normalizedReport = String(reportKey || '').toLowerCase()
  return LOCKED_REPORT_TABS[normalizedReport]?.has(String(tabName || '')) ?? false
}

export function getFreeAccessPeriod(
  detail: AccountDetail | null | undefined,
  account: AccountItem | null | undefined,
): FreeAccessPeriod {
  const detailSource = asRecord(detail)
  const accountSource = asRecord(account)
  const subscription = asRecord(detailSource?.subscription ?? accountSource?.subscription)
  const sources = [detailSource, accountSource, subscription]
  const startedAt = firstDate(sources, [
    'free_plan_started_at',
    'free_plan_start_date',
    'free_started_at',
    'plan_started_at',
    'subscription_started_at',
    'started_at',
    'start_date',
    'created_at',
    'createdAt',
  ])
  const explicitEnd = firstDate(sources, [
    'free_plan_ends_at',
    'free_plan_end_date',
    'free_plan_expires_at',
    'free_plan_expiry',
    'plan_ends_at',
    'plan_end_date',
    'subscription_ends_at',
    'subscription_end_date',
    'expires_at',
    'end_date',
  ])

  if (!startedAt && !explicitEnd) return { startedAt: null, endedAt: null }
  return {
    startedAt,
    // Compatibility fallback for accounts created before the backend exposed an explicit end.
    endedAt: explicitEnd ?? (startedAt ? addDays(startedAt, FREE_PLAN_DURATION_DAYS) : null),
  }
}

export function getFreeAccessSnapshot(
  detail: AccountDetail | null | undefined,
  account: AccountItem | null | undefined,
  now = new Date(),
): FreeAccessSnapshot {
  const period = getFreeAccessPeriod(detail, account)
  if (!period.endedAt) {
    return { ...period, status: 'unknown', daysLeft: null, elapsedDays: null }
  }

  const daysLeft = Math.max(0, Math.ceil((period.endedAt.getTime() - now.getTime()) / DAY_MS))
  const elapsedDays = period.startedAt
    ? Math.max(1, Math.floor((now.getTime() - period.startedAt.getTime()) / DAY_MS) + 1)
    : FREE_PLAN_DURATION_DAYS - daysLeft + 1

  if (now >= period.endedAt) return { ...period, status: 'ended', daysLeft: 0, elapsedDays }
  if (elapsedDays >= ENDING_START_DAY) return { ...period, status: 'ending', daysLeft, elapsedDays }
  if (elapsedDays >= COUNTDOWN_START_DAY) {
    return { ...period, status: 'countdown', daysLeft, elapsedDays }
  }
  return { ...period, status: 'active', daysLeft, elapsedDays }
}
